import { useMutation } from "@apollo/client"
import { yupResolver } from "@hookform/resolvers/yup"
import cn from "classnames"
import React, { useCallback, useEffect, useState } from "react"
import { type SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { graphql } from "@/api"
import FormForm from "@/common/components/form_form"
import FormInput from "@/common/components/form_input"
import yup from "@/common/yup"

const passwordResetDoc = graphql(`
  mutation PasswordReset($email: String!) {
    passwordReset(email: $email) {
      message

      errors {
        message
      }
    }
  }
`)

interface ForgotFormInputs {
  [key: string]: string | undefined | null
  forgotEmail: string
}

const forgotSchema = yup.object().shape<ForgotFormInputs>({
  forgotEmail: yup.string().required(),
})

interface ForgotFormProps {
  show: boolean
  email: string
}

const ForgotForm: React.FC<ForgotFormProps> = ({ show, email }) => {
  const { t } = useTranslation()
  const [mutation] = useMutation(passwordResetDoc)
  const formMethods = useForm<ForgotFormInputs>({
    resolver: yupResolver(forgotSchema),
    defaultValues: { forgotEmail: email },
  })
  const { handleSubmit, reset, formState } = formMethods
  const { isSubmitting } = formState

  useEffect(() => {
    reset({ forgotEmail: email })
  }, [reset, email])

  const [error, setError] = useState<string>()
  const [message, setMessage] = useState<string>()

  const submit: SubmitHandler<ForgotFormInputs> = useCallback(
    async (form, e) => {
      e?.preventDefault()

      setError(undefined)
      setMessage(undefined)
      const result = await mutation({ variables: { email: form.forgotEmail } })
      const data = result.data?.passwordReset

      if (data) {
        if (data.errors && data.errors[0] && data.errors.length > 0) {
          setError(data.errors[0].message)
        } else {
          setMessage(data.message || "")
        }
      }
    },
    [mutation]
  )

  return (
    <div id="retrieve_password" className={cn({ hidden: !show })}>
      <FormForm id="forgot_password" noValidate onSubmit={handleSubmit(submit)} formMethods={formMethods}>
        {error && <p className="form_message bg-error">{error}</p>}
        {message && <p className="form_message bg-success">{message}</p>}
        <FormInput type="email" name="forgotEmail" id="customer_forgot_email" placeholder={t("customer.email")} />
        <div className="center">
          <button
            id="customer_forgot_password"
            className="button button-primary button-medium full-width mb-0"
            type="submit"
            disabled={isSubmitting}
          >
            {t("customer.send_password")}
          </button>
        </div>
      </FormForm>
    </div>
  )
}

export default ForgotForm
